import React from 'react';
import { TrendingUp, TrendingDown, Sparkles, Clock, Users, Brain, Compass, ArrowUpRight, Heart } from 'lucide-react';

interface Pattern {
  id?: string;
  type: 'emotional' | 'behavioral' | 'temporal' | 'relational' | 'decision';
  title: string;
  description: string;
  strength: number;
  frequency: number;
  isPositive: boolean;
  insight?: string;
  coreConnection?: {
    type?: string;
    name?: string;
    alignment?: number;
  };
}

interface PatternClusterCardProps {
  pattern: Pattern;
  translations?: {
    timesObserved: string;
    connectedTo: string;
    insight: string;
    typeLabels?: {
      emotional: string;
      behavioral: string;
      temporal: string;
      relational: string;
      decision: string;
    };
  };
}

const typeConfig = {
  emotional: {
    icon: Heart,
    gradient: 'from-pink-500 to-rose-500',
    bgLight: 'bg-pink-50',
    border: 'border-pink-100',
    textColor: 'text-pink-600'
  },
  behavioral: {
    icon: Compass,
    gradient: 'from-blue-500 to-indigo-500',
    bgLight: 'bg-blue-50',
    border: 'border-blue-100',
    textColor: 'text-blue-600'
  },
  temporal: {
    icon: Clock,
    gradient: 'from-purple-500 to-violet-500',
    bgLight: 'bg-purple-50',
    border: 'border-purple-100',
    textColor: 'text-purple-600'
  },
  relational: {
    icon: Users,
    gradient: 'from-amber-500 to-orange-500',
    bgLight: 'bg-amber-50',
    border: 'border-amber-100',
    textColor: 'text-amber-600'
  },
  decision: {
    icon: Brain,
    gradient: 'from-emerald-500 to-teal-500',
    bgLight: 'bg-emerald-50',
    border: 'border-emerald-100',
    textColor: 'text-emerald-600'
  }
};

const defaultTranslations = {
  timesObserved: 'times observed',
  connectedTo: 'Connected to',
  insight: 'Insight',
  typeLabels: {
    emotional: 'Emotional',
    behavioral: 'Behavioral',
    temporal: 'Temporal',
    relational: 'Relational',
    decision: 'Decision'
  }
};

const PatternClusterCard: React.FC<PatternClusterCardProps> = ({
  pattern,
  translations = defaultTranslations
}) => {
  const config = typeConfig[pattern.type] || typeConfig.behavioral;
  const Icon = config.icon;
  const typeLabels = translations.typeLabels || defaultTranslations.typeLabels;
  const typeLabel = typeLabels[pattern.type] || typeLabels.behavioral;

  return (
    <div className={`group relative rounded-[20px] md:rounded-[24px] overflow-hidden transition-all duration-300 hover:shadow-lg hover:scale-[1.02] ${config.bgLight} ${config.border} border`}>
      <div className="absolute top-0 left-0 right-0 h-1">
        <div 
          className={`h-full bg-gradient-to-r ${config.gradient} rounded-full`}
          style={{ width: `${pattern.strength}%` }}
        />
      </div>

      <div className="p-4 md:p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className={`w-10 h-10 md:w-11 md:h-11 rounded-xl md:rounded-2xl bg-gradient-to-br ${config.gradient} flex items-center justify-center shrink-0 shadow-lg`}>
            {pattern.isPositive ? (
              <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-white" />
            ) : (
              <TrendingDown className="w-5 h-5 md:w-6 md:h-6 text-white" />
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <h4 className="text-[13px] md:text-[14px] font-bold text-black/85 leading-tight mb-1">
              {pattern.title}
            </h4>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] md:text-[11px] font-bold ${config.textColor}`}>
                {typeLabel}
              </span>
              <span className="text-[10px] md:text-[11px] text-black/30">•</span>
              <span className="text-[10px] md:text-[11px] font-medium text-black/40">
                {pattern.frequency}× {translations.timesObserved}
              </span>
            </div>
          </div>

          <div className={`shrink-0 px-2 md:px-2.5 py-1 md:py-1.5 rounded-xl font-bold text-[11px] md:text-[12px] ${
            pattern.isPositive 
              ? 'bg-emerald-500 text-white shadow-emerald-500/30' 
              : 'bg-amber-500 text-white shadow-amber-500/30'
          } shadow-lg`}>
            {pattern.strength}%
          </div>
        </div>

        <p className="text-[12px] md:text-[13px] text-black/60 leading-relaxed mb-3">
          {pattern.description}
        </p>

        {pattern.insight && (
          <div className="bg-white/70 rounded-xl p-3 mb-3 border border-black/5">
            <div className="flex items-start gap-2">
              <span className="text-sm shrink-0">💡</span>
              <p className="text-[11px] md:text-[12px] text-black/70 leading-relaxed italic">
                {pattern.insight}
              </p>
            </div>
          </div>
        )}

        {pattern.coreConnection?.name && (
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-black/40 font-medium">{translations.connectedTo}:</span>
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] md:text-[11px] font-medium ${
              (pattern.coreConnection.alignment ?? 0) > 0 
                ? 'bg-emerald-100 text-emerald-700' 
                : (pattern.coreConnection.alignment ?? 0) < 0 
                  ? 'bg-red-100 text-red-700'
                  : 'bg-gray-100 text-gray-600'
            }`}>
              <span>
                {pattern.coreConnection.type === 'value' ? '💎' : 
                 pattern.coreConnection.type === 'goal' ? '🎯' : 
                 pattern.coreConnection.type === 'blocker' ? '🚧' : '📍'}
              </span>
              <span>{pattern.coreConnection.name}</span>
              {pattern.coreConnection.alignment !== undefined && pattern.coreConnection.alignment !== 0 && (
                <span className="font-bold">
                  {pattern.coreConnection.alignment > 0 ? '+' : ''}{pattern.coreConnection.alignment}
                </span>
              )}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatternClusterCard;
